import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import * as Sentry from "@sentry/react";
import { useQueryClient } from "@tanstack/react-query";

import { useGetOrg } from "@src/api/organizations/get-organization";
import { organizationsKeys } from "@src/api/queryKeys";
import { isTerminalNonCompletedBillingCommandStatus } from "@src/utils/billingCommandStatus";
import { useWebsocket } from "@src/hooks/websocket";
import { GENERAL_ROUTES } from "@src/routes/routes";
import {
  mpBillingTrialOverviewViewed,
  mpBillingTrialSkipped,
  mpBillingTrialStarted,
} from "@src/utils/mixpanel";

import {
  type StartTrialStatus,
  StartTrialStepView,
} from "./StartTrialStep.view";
import { useStartTrial } from "./useStartTrial";

const TRIAL_HELP_SUBJECT = "Trouble starting my free trial";
const TRIAL_HELP_DESCRIPTION =
  "I ran into an error while starting my Intelligems free trial and need help getting set up.";
// A TestTypeKey from the Get Help support form (see pylon-support-form).
const TRIAL_HELP_TEST_TYPE = "no_test_type";

type StartTrialStepProps = {
  onTrialStarted: () => void;
  onTrialSkipped?: () => void;
  embedded?: boolean;
  walkthrough?: boolean;
};

export const StartTrialStep = ({
  onTrialStarted,
  onTrialSkipped,
  embedded,
  walkthrough = false,
}: StartTrialStepProps) => {
  // The full org id — the truncated `useGetOrgId()` does NOT match the websocket
  // message's `orgId` or the org-summary query key, both of which use the full id.
  const orgId = useGetOrg().data.org.id;
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    isError,
    isModularPricesLoading,
    isPending,
    startTrial,
    trialPriceIds,
    trialPrices,
  } = useStartTrial();
  const { lastJsonMessage } = useWebsocket();

  // The create command resolves 200 once it is *queued*; the trial is provisioned
  // asynchronously via Step Function. `awaitingCommandId` holds the loading state
  // across that window — until the derived phase advances and unmounts us, or the
  // command fails and we hand the user off to Get Help.
  const [awaitingCommandId, setAwaitingCommandId] = useState<string | null>(
    null
  );
  const [hasAsyncFailed, setHasAsyncFailed] = useState(false);
  const [asyncFailureReason, setAsyncFailureReason] = useState<string | null>(
    null
  );
  const advanceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const hasFailed = !walkthrough && (isError || hasAsyncFailed);

  // Stay in the loading view until the phase advance unmounts us or the failure
  // redirect fires — never flash the overview back at the user mid-flight.
  const status: StartTrialStatus = walkthrough
    ? "overview"
    : isPending || awaitingCommandId != null || hasFailed
      ? "loading"
      : "overview";

  // A Shopify/Stripe rejection of the queued trial surfaces only as a billing-
  // command update over the websocket, long after the create mutation returned
  // 200 — as a terminal failure status or an update carrying an error. Without
  // this the loading state would hang forever. Match on orgId + commandId so an
  // unrelated command can't trip it.
  useEffect(() => {
    if (awaitingCommandId == null) {
      return;
    }
    if (
      lastJsonMessage?.type === "billingCommandUpdate" &&
      lastJsonMessage.orgId === orgId &&
      lastJsonMessage.billingCommandId === awaitingCommandId &&
      (isTerminalNonCompletedBillingCommandStatus(lastJsonMessage.status) ||
        lastJsonMessage.errorCode != null ||
        lastJsonMessage.errorMessage != null)
    ) {
      // The create mutation returned 200, so this async failure is invisible to
      // the global mutation-error handler — report it here.
      Sentry.captureException(
        new Error(
          `Onboarding trial start failed: ${
            lastJsonMessage.errorCode ?? lastJsonMessage.status ?? "unknown"
          }`
        ),
        {
          tags: { feature: "onboarding_trial" },
          extra: {
            orgId,
            billingCommandId: lastJsonMessage.billingCommandId,
            status: lastJsonMessage.status,
            errorCode: lastJsonMessage.errorCode,
            errorMessage: lastJsonMessage.errorMessage,
          },
        }
      );
      setAwaitingCommandId(null);
      setAsyncFailureReason(lastJsonMessage.errorMessage ?? null);
      setHasAsyncFailed(true);
      if (advanceTimerRef.current != null) {
        clearTimeout(advanceTimerRef.current);
        advanceTimerRef.current = null;
      }
    }
  }, [lastJsonMessage, awaitingCommandId, orgId]);

  // On failure — synchronous rejection or async workflow failure — redirect to
  // Get Help with a prefilled support request rather than stranding the user on
  // the trial step.
  useEffect(() => {
    if (!hasFailed) {
      return;
    }
    const description = asyncFailureReason
      ? `${TRIAL_HELP_DESCRIPTION}\n\nError detail: ${asyncFailureReason}`
      : TRIAL_HELP_DESCRIPTION;
    const params = new URLSearchParams({
      subject: TRIAL_HELP_SUBJECT,
      description,
      testType: TRIAL_HELP_TEST_TYPE,
      ref: location.pathname,
    });
    navigate(`${GENERAL_ROUTES.GetHelp}?${params.toString()}`);
  }, [hasFailed, asyncFailureReason, location.pathname, navigate]);

  useEffect(() => {
    if (walkthrough || isModularPricesLoading || trialPrices.length === 0) {
      return;
    }

    const trialEndDate = new Date();
    trialEndDate.setDate(trialEndDate.getDate() + 14);

    mpBillingTrialOverviewViewed({
      modulesIncluded: trialPrices.map((p) => p.product.name),
      trialEndDate: trialEndDate.toISOString(),
    });
  }, [isModularPricesLoading, trialPrices, walkthrough]);

  // The org summary drives the server-derived phase; /v2/organization carries
  // the entitlements granted with the trial, which the next step ("Launch your
  // first experience") gates on.
  const invalidateOrgQueries = () => {
    void queryClient.invalidateQueries({
      queryKey: organizationsKeys.orgSummary(orgId),
    });
    void queryClient.invalidateQueries({ queryKey: organizationsKeys.one });
  };

  const handleSkipTrial = onTrialSkipped
    ? () => {
        mpBillingTrialSkipped({ source: "onboarding_flow" });
        onTrialSkipped();
      }
    : undefined;

  const handleStartTrial = () => {
    if (walkthrough) {
      onTrialStarted();
      return;
    }

    const started = startTrial({
      onSuccess: (data) => {
        mpBillingTrialStarted({
          // Smart Pricing is excluded from the trial allowlist (see TRIAL_INCLUDED_ENTITLEMENT_KEYS)
          isSmartPricingExcluded: true,
          modulesIncluded: trialPrices.map((p) => p.product.name),
          source: "onboarding_flow",
        });
        setAwaitingCommandId(data.commandId);
        // The trial subscription is created asynchronously via a Step
        // Function; the command resolves before Stripe confirms the trialing
        // subscription. Refetch now for the fast path, and again after the SF
        // has had time to land so the server-derived phase advances out of
        // `start_trial` and the new entitlements arrive. The delayed refetch
        // must outlive this component: the phase advance unmounts the step
        // before the timer fires on every successful run, so it is cleared
        // only on async failure, never on unmount. refetchOnWindowFocus
        // reconciles if the SF is still running past this window.
        invalidateOrgQueries();
        advanceTimerRef.current = setTimeout(invalidateOrgQueries, 10_000);
      },
    });

    if (started) onTrialStarted();
  };

  return (
    <StartTrialStepView
      embedded={embedded}
      handleStartTrial={handleStartTrial}
      isModularPricesLoading={walkthrough ? false : isModularPricesLoading}
      secondaryAction={
        handleSkipTrial
          ? { label: "Skip", onClick: handleSkipTrial }
          : undefined
      }
      status={status}
      trialPriceIds={walkthrough ? ["walkthrough"] : trialPriceIds}
    />
  );
};
